/**
 * EntityImageGallery — image strip + upload control for a detail page.
 *
 * Lists every image attachment linked to (entityType, entityId), shows
 * them as 4:3 cards, lets the user attach more, and supports per-item
 * delete. Used on VehicleDetailPage and AnimalDetailPage.
 *
 * Loading shows a single skeleton; if there are no images the upload
 * button itself is the empty state, sized to match a card so the layout
 * doesn't jump when the first image lands.
 */
import { useEffect, useState } from 'react';
import { ImagePlus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ImageUpload } from '@/components/ImageUpload';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  listAttachments,
  deleteAttachment,
  getAttachmentDownloadUrl,
  type Attachment,
} from '@/lib/attachments';
import { toast } from 'sonner';

interface EntityImageGalleryProps {
  entityType: string;
  entityId: string;
  /** Section title shown above the strip. Defaults to "Photos". */
  title?: string;
}

export function EntityImageGallery({ entityType, entityId, title }: EntityImageGalleryProps): JSX.Element {
  const [images, setImages] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshTick, setRefreshTick] = useState<number>(0);
  const [pendingDelete, setPendingDelete] = useState<Attachment | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listAttachments({ linked_entity_type: entityType, linked_entity_id: entityId, image_only: true })
      .then((items) => {
        if (!cancelled) setImages(items);
      })
      .catch(() => {
        if (!cancelled) setImages([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [entityType, entityId, refreshTick]);

  const refresh = (): void => setRefreshTick((t) => t + 1);

  const handleConfirmDelete = async (): Promise<void> => {
    if (!pendingDelete) return;
    try {
      await deleteAttachment(pendingDelete.id);
      toast.success('Image removed');
      setPendingDelete(null);
      refresh();
    } catch (err) {
      toast.error((err as Error).message ?? 'Failed to remove image');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-foreground">{title ?? 'Photos'}</h2>
        <ImageUpload entityType={entityType} entityId={entityId} onUploaded={refresh} />
      </div>

      {loading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          <Skeleton className="aspect-[4/3] w-full rounded-lg" />
        </div>
      ) : images.length === 0 ? (
        <button
          type="button"
          onClick={() => undefined}
          className="flex aspect-[4/3] w-full max-w-xs items-center justify-center rounded-lg border-2 border-dashed border-border bg-sand text-muted-foreground dark:bg-surface-raised-dark"
          aria-hidden="true"
          tabIndex={-1}
        >
          <div className="flex flex-col items-center gap-1 text-xs">
            <ImagePlus className="h-5 w-5" />
            No photos yet
          </div>
        </button>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {images.map((img) => (
            <figure key={img.id} className="group relative overflow-hidden rounded-lg border border-border bg-surface dark:bg-surface-dark">
              <img
                src={getAttachmentDownloadUrl(img.id)}
                alt={img.description ?? img.original_filename}
                className="aspect-[4/3] w-full object-cover"
                loading="lazy"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setPendingDelete(img)}
                aria-label="Remove image"
                className="absolute right-1.5 top-1.5 h-7 w-7 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100 bg-surface/80 dark:bg-surface-dark/80"
              >
                <Trash2 className="h-3.5 w-3.5 text-destructive" />
              </Button>
            </figure>
          ))}
        </div>
      )}

      {pendingDelete ? (
        <AlertDialog open={Boolean(pendingDelete)} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove this image?</AlertDialogTitle>
              <AlertDialogDescription>
                {pendingDelete.original_filename} will be removed from this record. The file stays on the server in case you need to undo.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmDelete} className="bg-destructive text-destructive-foreground">
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </div>
  );
}
